import React, { useEffect } from 'react'
import {
  X,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Trash2,
  Check,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import AuthenticatedImage from '@/components/AuthenticatedImage'

interface MobileCatalogImagePreviewProps {
  imageUrl: string
  imageIndex: number
  totalImages: number
  isAdmin: boolean
  isThumbnail: boolean
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onSetThumbnail: () => void
  onDelete: () => void
}

const MobileCatalogImagePreview: React.FC<MobileCatalogImagePreviewProps> = ({
  imageUrl,
  imageIndex,
  totalImages,
  isAdmin,
  isThumbnail,
  onClose,
  onPrev,
  onNext,
  onSetThumbnail,
  onDelete,
}) => {
  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          e.preventDefault()
          onPrev()
          break
        case 'ArrowRight':
          e.preventDefault()
          onNext()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, onPrev, onNext])

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 backdrop-blur-sm safe-area-top">
        <button
          onClick={onClose}
          className="p-2 -ml-2 text-white active:opacity-70 rounded-lg"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="text-white font-medium">
          {imageIndex + 1} / {totalImages}
        </div>

        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Image Container */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {/* Main Image */}
        <AuthenticatedImage
          imageUrl={imageUrl}
          alt={`Image ${imageIndex + 1}`}
          className="max-w-full max-h-full object-contain"
          size="large"
        />

        {/* Navigation Arrows */}
        {totalImages > 1 && (
          <>
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full active:bg-black/70 transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full active:bg-black/70 transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Thumbnail Badge */}
        {isThumbnail && (
          <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-white rounded-full text-sm font-medium">
            <Check className="h-4 w-4" />
            <span>Thumbnail</span>
          </div>
        )}
      </div>

      {/* Action Bar */}
      {isAdmin && (
        <div className="bg-gray-900/80 backdrop-blur-sm px-4 py-4 safe-area-bottom">
          <div className="flex items-center justify-center gap-4">
            {/* Set as Thumbnail */}
            <button
              onClick={onSetThumbnail}
              disabled={isThumbnail}
              className={cn(
                'flex flex-col items-center gap-1 px-6 py-2 rounded-xl transition-colors',
                isThumbnail
                  ? 'text-gray-500'
                  : 'text-white active:bg-white/10'
              )}
            >
              <div
                className={cn(
                  'p-3 rounded-full',
                  isThumbnail ? 'bg-primary-500/30' : 'bg-white/10'
                )}
              >
                <ImageIcon className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium">
                {isThumbnail ? 'Is Thumbnail' : 'Set Thumbnail'}
              </span>
            </button>

            {/* Delete */}
            <button
              onClick={onDelete}
              className="flex flex-col items-center gap-1 px-6 py-2 rounded-xl text-red-400 active:bg-white/10 transition-colors"
            >
              <div className="p-3 rounded-full bg-red-500/20">
                <Trash2 className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium">Delete</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileCatalogImagePreview
